import React from 'react'
import { connect } from 'react-redux'

import { Avatar } from 'components/styled/avatar'
import uniqueId from 'lodash/uniqueId'
import { mapLanguageToComponentProps } from 'admin/i18n'
import { earlierDate, formatDateTime } from 'utils/date'
import { hasFeature } from 'admin/selectors/session'
import ScheduledInterviewStatus from 'components/scheduled-interview-status'
import {
  Info,
  ExternalLink,
  Small,
  Body,
  LinkStyleButton,
  H3,
} from 'components/styled/typography'
import { Field } from 'components/styled/typography/field'
import { I18nComponentType } from 'admin/i18n-types'
import { Flex } from 'components/styled/containers/flex'
import styled from 'styled-components'
import { Box } from 'components/styled/containers/box'
import { Tag } from 'components/styled/tag'
import MessageDeliveryStatus from 'components/message-delivery-status'
import { StageType } from 'types'
import StageContent from 'admin/components/stage-content'
import PersonState from 'components/person-state'

export interface PersonProps {
  name: string
  stage: StageType
  completed?: boolean
  state?: string
  city?: string
  email: string
  phone: string
  links?: string[]
  link?: string
  attachments?: {
    name: string
    url: string
    path: string
  }[]
  avatar?: string
  lastAccess?: string
  coverLetter?: string
  additionalFieldResponses?: {
    _id: string
    fieldId: string
    answer: string
  }[]
  linkedIn?: {
    pictureUrl?: string
  }
  emailDeliveryStatus?: {
    bounceType: string
    bounceSubType: string
  }
  smsDeliveryStatus?: string
  referrer?: string
  answerDeadline?: string
}

interface PersonDetailsProps {
  context?: string
  isIntroduceeAdmin?: boolean
  hasFeatureCalendarEnabled?: boolean
  i18n: I18nComponentType
  personIndex?: number
  otherPersons?: string[]
  toggleOtherPersonsModal?: () => void
  person: PersonProps
  position: {
    stages?: StageType[]
    endTime?: string
    additionalFormFields?: {
      _id: string
      name: string
      type: 'input' | 'textarea' | 'checkbox' | 'select'
      options: { value: string }[]
    }[]
  }
  calendarInvitationStatus?: {
    status: string
    eventStartTime: string
    eventLocation: string
  }[]
}

const AvatarWrapper = styled.div`
  max-width: 50%;

  ${({ theme }) => theme.mediaQuery.tablet} {
    max-width: 100%;
    margin-top: ${({ theme }) => theme.space.s16};
  }
`

const DetailsWrapper = styled(Flex)`
  ${({ theme }) => theme.mediaQuery.tablet} {
    display: block;
  }
`

const getSortedAnswers = (options: { value: string }[], answer: string[]) => {
  const result = []
  for (let i = 0; i < options.length; i += 1) {
    const optionIndex = answer.indexOf(options[i].value)
    if (optionIndex > -1) {
      result.push(answer[optionIndex])
    }
  }
  return result
}

export function PersonDetails({
  person,
  i18n,
  position,
  context,
  isIntroduceeAdmin = false,
  hasFeatureCalendarEnabled = false,
  calendarInvitationStatus = [],
  personIndex,
  otherPersons,
  toggleOtherPersonsModal,
}: PersonDetailsProps) {
  const displayedDeadline = earlierDate(
    person.answerDeadline,
    context === 'interview' ? position.endTime : undefined
  )
  const additionalFields = position.additionalFormFields || []
  const isCompleted = person.completed || person.state === 'completed'

  const shouldRenderAdditionalFields =
    additionalFields.length > 0 &&
    person.additionalFieldResponses &&
    person.additionalFieldResponses.length > 0 &&
    isCompleted

  const avatar =
    person.avatar ||
    (person.linkedIn && 'pictureUrl' in person.linkedIn
      ? person.linkedIn.pictureUrl
      : undefined)

  const namePlaceholder =
    typeof personIndex === 'number'
      ? `${i18n.personDetails.namePlaceholder} ${personIndex + 1}`
      : undefined

  return (
    <Box mx="s4">
      <DetailsWrapper>
        <Box style={{ flexGrow: '1', paddingRight: '16px' }}>
          {context === 'live' ? (
            <>
              {person.name && (
                <Field title={i18n.personDetails.name} content={person.name} />
              )}
              {person.email && (
                <Field
                  title={i18n.personDetails.email}
                  content={person.email}
                />
              )}
            </>
          ) : (
            <>
              {(person.name || namePlaceholder) && (
                <Field
                  title={i18n.personDetails.name}
                  content={
                    <>
                      <Info>{person.name || namePlaceholder}</Info>
                      <Body>
                        {otherPersons && otherPersons.length > 0 && (
                          <>
                            <LinkStyleButton
                              onClick={toggleOtherPersonsModal}
                              className="otherPersons-link"
                            >
                              {i18n.personDetails.otherPersons(
                                otherPersons.length
                              )}
                            </LinkStyleButton>
                          </>
                        )}
                      </Body>
                    </>
                  }
                />
              )}
              {person.city && (
                <Field title={i18n.personDetails.city} content={person.city} />
              )}
              {person.email && (
                <Field
                  title={i18n.personDetails.email}
                  content={
                    <>
                      <Body>{person.email}</Body>
                      {person.emailDeliveryStatus ? (
                        <MessageDeliveryStatus
                          bounceType={person.emailDeliveryStatus.bounceType}
                          bounceSubType={
                            person.emailDeliveryStatus.bounceSubType
                          }
                        />
                      ) : null}
                    </>
                  }
                />
              )}
              {person.phone && (
                <Field
                  title={i18n.personDetails.phone}
                  content={
                    <>
                      <Body>{person.phone}</Body>
                      {person.smsDeliveryStatus && (
                        <MessageDeliveryStatus
                          smsStatus={person.smsDeliveryStatus}
                        />
                      )}
                    </>
                  }
                />
              )}
              {!isIntroduceeAdmin && (
                <Field
                  ellipsis={false}
                  title={i18n.searchFilter.headings.stage}
                  content={
                    <StageContent
                      stage={person.stage}
                      positionStages={position.stages}
                    />
                  }
                />
              )}
              <ul className="list-unstyled">
                {((person.links && person.links.length > 0) || person.link) && (
                  <div>
                    <Field
                      title={i18n.personDetails.links}
                      content={
                        <>
                          {person.links?.map((link) => (
                            <li key={link}>
                              <ExternalLink
                                target="_blank"
                                rel="noopener noreferrer"
                                href={link}
                              >
                                {link}
                              </ExternalLink>
                            </li>
                          ))}
                          {person.link && (
                            <li key={person.link}>
                              <ExternalLink
                                target="_blank"
                                rel="noopener noreferrer"
                                href={person.link}
                              >
                                {person.link}
                              </ExternalLink>
                            </li>
                          )}
                        </>
                      }
                    />
                  </div>
                )}
              </ul>
              <ul className="list-unstyled">
                {person.attachments && person.attachments.length > 0 && (
                  <Field
                    title={i18n.personDetails.attachments}
                    content={
                      <>
                        {person.attachments.map((file) => (
                          <li key={file.path}>
                            <ExternalLink
                              target="_blank"
                              rel="noopener noreferrer"
                              href={file.url}
                            >
                              {file.name}
                            </ExternalLink>
                          </li>
                        ))}
                      </>
                    }
                  />
                )}
              </ul>
              {person.referrer && (
                <Field
                  ellipsis={false}
                  title={i18n.personDetails.referrer}
                  subtitle={`(${i18n.personDetails.referrerInfo})`}
                  content={person.referrer}
                />
              )}
              <ScheduledInterviewStatus
                hasFeatureCalendarEnabled={hasFeatureCalendarEnabled}
                calendarInvitationStatus={calendarInvitationStatus}
              />
            </>
          )}
        </Box>
        <AvatarWrapper>
          <Avatar
            size={context === 'live' ? 'largeMedium' : 'large'}
            isRound={context === 'live'}
            filename={avatar}
          />
          {!isIntroduceeAdmin && (
            <PersonState
              context={context}
              state={person.state}
              lastAccess={person.lastAccess}
            />
          )}
          {!isIntroduceeAdmin && person.answerDeadline && !person.completed && (
            <Small
              color={
                new Date(displayedDeadline) < new Date()
                  ? 'warn110'
                  : 'bodyText'
              }
              mt="s4"
            >
              {new Date(displayedDeadline) < new Date()
                ? i18n.personDetails.deadlineExpired
                : i18n.personDetails.deadline(
                    formatDateTime(displayedDeadline)
                  )}
            </Small>
          )}
        </AvatarWrapper>
      </DetailsWrapper>
      {person.coverLetter && (
        <Field
          ellipsis={false}
          title={i18n.personDetails.coverLetter}
          content={person.coverLetter}
        />
      )}
      {shouldRenderAdditionalFields && (
        <Box mt="s32">
          <Box mb="s20">
            <H3>{i18n.personDetails.headerAdditionalInformation}</H3>
          </Box>
          {additionalFields?.map((field) => {
            const answerItem = person.additionalFieldResponses?.find(
              (item) => item.fieldId === field._id
            )
            if (answerItem && answerItem.answer) {
              const sortedAnswers =
                typeof answerItem.answer === 'string'
                  ? [answerItem.answer]
                  : getSortedAnswers(field.options, answerItem.answer)
              const answerElements = sortedAnswers.map((answerItem) => {
                switch (field.type) {
                  case 'checkbox':
                    return (
                      <Tag
                        icon="check_circle"
                        key={uniqueId(answerItem)}
                        text={answerItem}
                      />
                    )
                  case 'select':
                    return (
                      <Tag
                        icon="radio_button_checked"
                        key={uniqueId(answerItem)}
                        text={answerItem}
                      />
                    )
                  case 'input':
                  case 'textarea':
                  default:
                    return (
                      <Body key={uniqueId(answerItem)} whiteSpace="pre-wrap">
                        {answerItem}
                      </Body>
                    )
                }
              })
              return (
                <Field
                  key={field._id}
                  title={field.name}
                  content={
                    <Flex gap="s4" flexWrap="wrap">
                      {answerElements}
                    </Flex>
                  }
                />
              )
            }
            return null
          })}
        </Box>
      )}
    </Box>
  )
}

export default connect<{ i18n: I18nComponentType }>(
  mapLanguageToComponentProps((state: any) => ({
    hasFeatureCalendarEnabled: hasFeature(state, 'calendar'),
  }))
)(PersonDetails)
